import { useAiEditorMode } from '@/composables/ai-upload/useAiEditorMode';
import { useAiPersonalizationControls } from '@/composables/ai-upload/useAiPersonalizationControls';
import { useAiGenerationHistoryStore } from '@/stores/ai-upload/AiGenerationHistoryStore';

type AiTextEditorLauncher = {
    openTextEditor: () => void;
    editBakedText: (id?: string) => Promise<void>;
};

// Shared by the panel's "Naam/tekst" row and the sheet footer's CTA so both land in the same state.
export function useAiTextEditorLauncher(): AiTextEditorLauncher {
    const { store, onAddItem, onEditBakedText } = useAiPersonalizationControls();
    const historyStore = useAiGenerationHistoryStore();
    const { setMode } = useAiEditorMode();

    function openTextEditor(): void {
        // First, so backing out of the visit also removes the placeholder seeded below.
        store.beginTextEditingSession();

        store.isOpen = true;

        // Text belongs to the live generation — the editor must not open over a history version.
        historyStore.resetSelection();

        // Jump to the face the text sits on; the customer may have swiped to a detail shot since.
        store.focusBakeTarget();

        if (store.hasBakedText) {
            return;
        }

        if (store.items.length === 0) {
            onAddItem();
        }

        if (store.selectedId === undefined) {
            store.selectedId = store.items[0]?.id;
        }

        store.expandedId = store.selectedId;
        setMode('style');
    }

    // onEditBakedText clears the commit before its first await, so the editor opens on editable lines without waiting for the round trip.
    async function editBakedText(id?: string): Promise<void> {
        const unbaking = onEditBakedText(id);

        openTextEditor();

        await unbaking;
    }

    return { openTextEditor, editBakedText };
}
